import {
  CalendarIcon,
  ExternalLinkIcon,
  Pencil1Icon,
  TrashIcon,
} from "@radix-ui/react-icons";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangleIcon, CheckIcon } from "lucide-react";
import { useState } from "react";

import {
  AddStickyPostButton,
  AddStickPostOptions,
} from "@/components/AddStickyPostsButton";
import { useToast } from "@/components/hooks/use-toast";
import { Page } from "@/components/Page";
import { Badge } from "@/components/ui/badge";
import { Button, ButtonLink } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { NumberInput } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { H1, P } from "@/components/ui/typography";
import {
  addPostToStickySet,
  editPostInStickySet,
  getStickySetQueryOptions,
  reconcileStickySet,
  removeFeedFromStickySet,
  removePostFromStickySet,
  StickySetDetail,
  stickySetDriftQueryOptions,
  StickySetMember,
  updateStickySet,
} from "@/lib/api";
import { atUriToBskyUrl, cn } from "@/lib/utils";

export const Route = createFileRoute(
  "/app/_sidebar-layout/sticky-sets/$setId/view",
)({
  component: StickySetPage,
  loader: ({ context: { queryClient }, params: { setId } }) => {
    void queryClient.prefetchQuery(getStickySetQueryOptions(parseInt(setId)));
  },
  head: () => ({ meta: [{ title: "Edit Sticky Set" }] }),
});

function Panel({
  title,
  action,
  children,
}: {
  title: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg",
        "bg-slate-subtle border border-slate-6 dark:border-slatedark-6",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between gap-4 pl-4 pr-2 py-2 h-12",
          "border-b border-slate-6 dark:border-slatedark-6",
        )}
      >
        <div className="font-medium flex items-center gap-3">{title}</div>
        <div className="flex gap-1">{action}</div>
      </div>
      {children}
    </div>
  );
}

function TypeBadge({ type }: { type: StickySetMember["sticky_type"] }) {
  return type === "pinned" ? (
    <Badge variant="default">Pinned</Badge>
  ) : (
    <Badge variant="secondary">Rotating</Badge>
  );
}

function ExpiryCell({ member }: { member: StickySetMember }) {
  const expiry = member.stopping_criteria?.expiration_time;
  if (!expiry) return <span className="text-slate-dim">&mdash;</span>;
  const date = new Date(expiry * 1000);
  const past = date.getTime() < Date.now();
  return (
    <span className={past ? "text-slate-dim line-through" : "text-slate-dim"}>
      {Intl.DateTimeFormat("en-us").format(date)}
    </span>
  );
}

/**
 * Change one post's settings for every feed in the set at once.
 *
 * This is the "edit it in one place" the whole feature exists for: type, order
 * and expiry are the four knobs the per-feed panel has always had, and setting
 * them here propagates to each feed on the next reconcile instead of being
 * clicked 38 times.
 */
function EditMemberDialog({
  set,
  member,
}: {
  set: StickySetDetail;
  member: StickySetMember;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [stickyType, setStickyType] = useState<"pinned" | "injected">(
    member.sticky_type,
  );
  const [order, setOrder] = useState<number | undefined>(
    member.order ?? undefined,
  );
  const [expiration, setExpiration] = useState<Date | undefined>(() =>
    member.stopping_criteria?.expiration_time
      ? new Date(member.stopping_criteria.expiration_time * 1000)
      : undefined,
  );
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      editPostInStickySet(set.id, member.id, {
        sticky_type: stickyType,
        order,
        // null clears a previously set expiry; undefined leaves it alone
        expiration: expiration ?? (member.stopping_criteria?.expiration_time ? null : undefined),
      }),
    onSuccess: (data) => {
      setOpen(false);
      toast({
        title: "Post updated",
        description: `${data.reconcile.update} ${
          data.reconcile.update === 1 ? "feed" : "feeds"
        } brought in line.`,
      });
    },
    onError: (error) =>
      toast({
        title: "Couldn't update that post",
        description: error.message,
        variant: "destructive",
      }),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <SimpleTooltip message="Edit this post everywhere in the set">
        <DialogTrigger asChild>
          <Button variant="outline" size="smIcon">
            <span className="sr-only">Edit post</span>
            <Pencil1Icon />
          </Button>
        </DialogTrigger>
      </SimpleTooltip>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit post in &ldquo;{set.display_name}&rdquo;</DialogTitle>
          <DialogDescription>
            Applies to all {set.feed_count}{" "}
            {set.feed_count === 1 ? "feed" : "feeds"} in this set. Feeds where
            you turned this post off individually stay off.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <Label htmlFor="member-type">Type</Label>
            <Select
              value={stickyType}
              onValueChange={(v) => setStickyType(v as "pinned" | "injected")}
            >
              <SelectTrigger id="member-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pinned">Pinned</SelectItem>
                <SelectItem value="injected">Rotating</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="member-order">Order (optional)</Label>
            <NumberInput
              id="member-order"
              value={order}
              onValueChange={setOrder}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>Expiration (optional)</Label>
            <div className="flex gap-2">
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start flex-1">
                    <CalendarIcon />
                    {expiration
                      ? Intl.DateTimeFormat("en-us").format(expiration)
                      : "No expiry"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 flex">
                  <Calendar
                    mode="single"
                    className="flex-1"
                    selected={expiration}
                    onSelect={(d) => {
                      setExpiration(d);
                      setDatePickerOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
              {expiration && (
                <Button variant="ghost" onClick={() => setExpiration(undefined)}>
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button isLoading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembersPanel({ set }: { set: StickySetDetail }) {
  const { toast } = useToast();

  const addPost = useMutation({
    mutationFn: (options: AddStickPostOptions) =>
      addPostToStickySet(set.id, {
        post_url: options.post_url,
        sticky_type: options.sticky_type,
        expiration: options.expiration,
        order: options.order,
      }),
    onSuccess: (data) => {
      toast({
        title: "Post added",
        description: `Now on ${data.reconcile.desired > 0 ? set.feed_count : 0} ${
          set.feed_count === 1 ? "feed" : "feeds"
        }.`,
      });
    },
    onError: (error) =>
      toast({
        title: "Couldn't add that post",
        description: error.message,
        variant: "destructive",
      }),
  });

  const removePost = useMutation({
    mutationFn: (memberId: number) => removePostFromStickySet(set.id, memberId),
    onSuccess: () =>
      toast({
        title: "Post removed",
        description: "Taken off every feed in this set.",
      }),
  });

  return (
    <Panel
      title={
        <>
          Posts in this set
          <span className="text-slate-dim text-sm tabular-nums">
            {set.members.length}
          </span>
        </>
      }
      action={
        <AddStickyPostButton
          onAddStickyPost={async (options) => {
            await addPost.mutateAsync(options);
          }}
        />
      }
    >
      {set.members.length === 0 ? (
        <div className="text-slate-dim text-sm p-4">
          No posts yet. Add one and it goes out to every feed in this set.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Post</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {set.members.map((member) => (
              <TableRow key={member.id} className="group">
                <TableCell>
                  {member.uri ? (
                    <a
                      href={atUriToBskyUrl(member.uri)}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 hover:underline"
                    >
                      <span className="font-mono text-xs truncate max-w-[22rem]">
                        {member.uri.split("/").pop()}
                      </span>
                      <ExternalLinkIcon className="h-3 w-3 shrink-0" />
                    </a>
                  ) : (
                    <span className="text-slate-dim">Unknown post</span>
                  )}
                  {!member.is_active && (
                    <Badge variant="secondary" className="ml-2">
                      Off
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <TypeBadge type={member.sticky_type} />
                </TableCell>
                <TableCell className="tabular-nums text-slate-dim">
                  {member.order ?? "—"}
                </TableCell>
                <TableCell>
                  <ExpiryCell member={member} />
                </TableCell>
                <TableCell>
                  <span className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <EditMemberDialog set={set} member={member} />
                    <SimpleTooltip message="Remove from set (takes it off every feed)">
                      <Button
                        variant="outline"
                        size="smIcon"
                        isLoading={
                          removePost.isPending &&
                          removePost.variables === member.id
                        }
                        onClick={() => removePost.mutate(member.id)}
                      >
                        <span className="sr-only">Remove from set</span>
                        <TrashIcon />
                      </Button>
                    </SimpleTooltip>
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Panel>
  );
}

function FeedsPanel({ set }: { set: StickySetDetail }) {
  const { toast } = useToast();
  const removeFeed = useMutation({
    mutationFn: (feedId: number) => removeFeedFromStickySet(set.id, feedId),
    onSuccess: () =>
      toast({
        title: "Feed removed",
        description: "This set's posts have been taken off it.",
      }),
  });

  const byTag = set.scope_mode === "tag";

  return (
    <Panel
      title={
        <>
          Feeds this set applies to
          <span className="text-slate-dim text-sm tabular-nums">
            {set.feeds.length}
          </span>
        </>
      }
      action={
        <ButtonLink variant="ghost" size="sm" to="/app">
          Add feeds
        </ButtonLink>
      }
    >
      {byTag && (
        <div className="px-4 py-3 text-sm text-slate-dim border-b border-slate-6 dark:border-slatedark-6">
          These feeds come from a tag, so any feed you give that tag joins
          automatically. To change the list, change which feeds carry the tag.
        </div>
      )}
      {set.feeds.length === 0 ? (
        <div className="text-slate-dim text-sm p-4">
          {byTag
            ? "No feeds carry this set's tag yet."
            : "No feeds yet. Select feeds on your feeds list and choose “Add to sticky set”."}
        </div>
      ) : (
        <ul className="divide-y divide-slate-6 dark:divide-slatedark-6">
          {set.feeds.map((feed) => (
            <li
              key={feed.id}
              className="group flex items-center justify-between gap-3 px-4 py-2"
            >
              <Link
                to="/app/feed/$feedId/view"
                params={{ feedId: `${feed.id}` }}
                className="hover:underline truncate"
              >
                {feed.display_name}
              </Link>
              {!byTag && (
                <span className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                  <SimpleTooltip message="Remove this feed from the set">
                    <Button
                      variant="outline"
                      size="smIcon"
                      isLoading={
                        removeFeed.isPending && removeFeed.variables === feed.id
                      }
                      onClick={() => removeFeed.mutate(feed.id)}
                    >
                      <span className="sr-only">Remove feed from set</span>
                      <TrashIcon />
                    </Button>
                  </SimpleTooltip>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Drift: feeds that are missing posts this set says they should have.
 *
 * Worth surfacing prominently, because it is the state hand-editing always ends
 * up in and there was previously no way to see it at all.
 */
function DriftCallout({ setId }: { setId: number }) {
  const { toast } = useToast();
  const { data: drift } = useQuery(stickySetDriftQueryOptions(setId));
  const apply = useMutation({
    mutationFn: () => reconcileStickySet(setId),
    onSuccess: (result) =>
      toast({
        title: "Set applied",
        description: `${result.inserted} added, ${result.counts.update} updated across ${result.feeds_touched} feeds.`,
      }),
    onError: (error) =>
      toast({
        title: "Couldn't apply the set",
        description: error.message,
        variant: "destructive",
      }),
  });

  if (!drift) return null;

  const missingFeeds = drift.feeds_missing_posts.length;
  const inSync =
    drift.counts.insert === 0 &&
    drift.counts.update === 0 &&
    drift.counts.delete === 0;

  if (inSync) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-dim">
        <CheckIcon className="h-4 w-4 text-green-normal" />
        Every feed in this set is up to date
        {drift.opted_out_rows.length > 0 && (
          <>
            {" "}
            &mdash; {drift.opted_out_rows.length}{" "}
            {drift.opted_out_rows.length === 1 ? "post has" : "posts have"} been
            turned off on individual feeds
          </>
        )}
        .
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-yellow-6 dark:border-yellowdark-6 bg-yellow-2 dark:bg-yellowdark-2 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <AlertTriangleIcon className="h-4 w-4 shrink-0 text-yellow-normal" />
      <div className="flex-1 flex flex-col gap-1">
        <div className="font-medium">
          {missingFeeds > 0
            ? `${missingFeeds} ${missingFeeds === 1 ? "feed is" : "feeds are"} missing posts from this set`
            : "This set has changes that haven't gone out yet"}
        </div>
        <P className="text-sm">
          {drift.counts.insert > 0 && (
            <>
              {drift.counts.insert} to add
              {drift.counts.update > 0 || drift.counts.delete > 0 ? ", " : ""}
            </>
          )}
          {drift.counts.update > 0 && (
            <>
              {drift.counts.update} to update
              {drift.counts.delete > 0 ? ", " : ""}
            </>
          )}
          {drift.counts.delete > 0 && <>{drift.counts.delete} to remove</>}.
        </P>
      </div>
      <Button isLoading={apply.isPending} onClick={() => apply.mutate()}>
        Apply now
      </Button>
    </div>
  );
}

function StickySetPage() {
  const { setId } = Route.useParams();
  const id = parseInt(setId);
  const { toast } = useToast();
  const { data: set } = useSuspenseQuery(getStickySetQueryOptions(id));

  const togglePause = useMutation({
    mutationFn: () => updateStickySet(id, { is_active: !set.is_active }),
    onSuccess: () =>
      toast({
        title: set.is_active ? "Set paused" : "Set resumed",
        description: set.is_active
          ? "Its posts are hidden on every feed, but nothing was deleted."
          : "Its posts are showing again.",
      }),
  });

  return (
    <Page className="md:gap-10">
      <div className="flex flex-wrap items-center gap-3">
        <H1>{set.display_name}</H1>
        {set.scope_mode === "tag" ? (
          <Badge variant="secondary">By tag</Badge>
        ) : (
          <Badge variant="outline">Chosen feeds</Badge>
        )}
        {!set.is_active && <Badge variant="warning">Paused</Badge>}
        <div className="flex-1" />
        <Button
          variant="outline"
          isLoading={togglePause.isPending}
          onClick={() => togglePause.mutate()}
        >
          {set.is_active ? "Pause set" : "Resume set"}
        </Button>
      </div>

      <div className="flex flex-col gap-6">
        {set.description && <P>{set.description}</P>}
        <DriftCallout setId={id} />
        <MembersPanel set={set} />
        <FeedsPanel set={set} />
      </div>
    </Page>
  );
}
